<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import LoginFormField from '@/features/auth/LoginFormField.vue';
import {useIdentShortPoll} from '@/features/auth/composables/useIdentShortPoll';
import {normalisePhone} from '@/features/auth-ceremony/normalisePhone';
import type {
    MobileIdCeremonyApi,
    MobileIdCeremonyHandle,
    MobileIdCeremonyInitiateResponse,
    MobileIdCeremonySuccess
} from '@/features/auth-ceremony/types';

defineOptions({name: 'MobileIdCeremony'});

const props = withDefaults(defineProps<{
    translations: Record<string, string>;
    apiConfig: MobileIdCeremonyApi;
    /** When provided, the identity input is hidden and this value is used as the personal code. */
    rememberedIdentityNumber?: string | null;
    /** When provided, pre-fills the phone field (raw, with or without +372 prefix). */
    prefillPhone?: string | null;
    /** Data-test id prefix used by the e2e suite. Login uses "mid", binding uses "bind-mid". */
    testIdPrefix?: string;
}>(), {
    rememberedIdentityNumber: null,
    prefillPhone: null,
    testIdPrefix: 'mid'
});

const identityNumber = defineModel<string>('identityNumber', {default: ''});

const emit = defineEmits<{
    success: [payload: MobileIdCeremonySuccess];
    errorCode: [code: string | null];
}>();

const shortPoll = useIdentShortPoll();

const phoneNumber = ref('');
const verificationCode = ref<string | null>(null);
const phoneError = ref('');
const identityError = ref('');
const isInitiating = ref(false);

const phoneInput = ref<HTMLInputElement | null>(null);
const identityInput = ref<HTMLInputElement | null>(null);

watch(phoneNumber, () => { phoneError.value = ''; });
watch(identityNumber, () => { identityError.value = ''; });

// Mirror remembered phone into the local input so submit logic and the API
// contract keep working unchanged. Strip the +372 country code if it was
// stored that way historically — the visible input shows the local part
// only, with the prefix rendered as a separate span.
watch(() => props.prefillPhone, (raw, prev) => {
    if (raw) {
        phoneNumber.value = raw.startsWith('+372') ? raw.slice(4) : raw;
    } else if (prev) {
        phoneNumber.value = '';
    }
}, {immediate: true});

// Personal code may be shared with Smart-ID via the parent's v-model. Only
// touch it when memory changes — never wipe user-typed values that may have
// been entered on the other tab.
watch(() => props.rememberedIdentityNumber, (code, prev) => {
    if (code) {
        identityNumber.value = code;
    } else if (prev) {
        identityNumber.value = '';
    }
}, {immediate: true});

const showPersonalCodeField = computed(() => !props.rememberedIdentityNumber);

function t(key: string, fallback?: string): string {
    const value = props.translations[key];
    if (value && value !== key.toLowerCase()) return value;
    return fallback ?? key;
}

const errorMessage = computed(() => {
    const code = shortPoll.errorCode.value;
    if (!code) return '';
    if (code === 'NetworkError') return t('auth-interror');
    if (code === 'Aborted') return '';
    if (code === 'TooManyRequests') return t('lbl-too-many-login-requests');
    // Cancellation is a deliberate user action — don't tell them off about it.
    // Match all casings Merit emits (snake-upper from the v1 layer, PascalCase
    // from the v3 layer) so the form re-renders silently, the same way the
    // in-page Cancel button does via the 'Aborted' code above.
    if (code === 'USER_CANCEL' || code === 'USER_CANCELLED' ||
        code === 'UserCancel' || code === 'UserCancelled') return '';
    if (code === 'TIMEOUT' || code === 'EXPIRED_TRANSACTION') return t('auth-expire');
    if (code === 'PHONE_ABSENT' || code === 'SIM_ERROR') return t('auth-phoneabsent');
    if (code === 'NOT_MID_CLIENT' || code === 'NO_CERTIFICATES') return t('auth_301');
    if (code === 'MissingParameters') return t('lbl-field-required');
    if (code === 'AuthIdentityNotLinked') return t('lbl-auth-identity-not-linked');
    return t('auth-interror');
});

const isWaiting = computed(() => shortPoll.phase.value === 'waiting');
const isBusyState = computed(() => isInitiating.value || isWaiting.value || !!verificationCode.value);

async function validate(): Promise<boolean> {
    phoneError.value = !phoneNumber.value.trim() ? t('lbl-field-required') : '';
    identityError.value = showPersonalCodeField.value && !identityNumber.value.trim() ? t('lbl-field-required') : '';
    if (phoneError.value || identityError.value) {
        shortPoll.errorCode.value = null;
        await nextTick();
        (phoneError.value ? phoneInput.value : identityInput.value)?.focus();
        return false;
    }
    return true;
}

async function start(): Promise<void> {
    if (!(await validate())) return;

    isInitiating.value = true;
    try {
        const result = await props.apiConfig.initiate({
            phoneNumber: normalisePhone(phoneNumber.value),
            identityNumber: identityNumber.value.trim()
        });

        if (result.retryAfterSeconds != null) {
            shortPoll.errorCode.value = 'TooManyRequests';
            emit('errorCode', 'TooManyRequests');
            return;
        }

        if (result.networkError) {
            shortPoll.errorCode.value = 'NetworkError';
            emit('errorCode', 'NetworkError');
            return;
        }

        if (!result.ok || !result.data) {
            shortPoll.errorCode.value = result.errorCode ?? null;
            emit('errorCode', result.errorCode ?? null);
            return;
        }

        verificationCode.value = result.data.verificationCode ?? null;
        await pollForCompletion(result.data);
    } finally {
        isInitiating.value = false;
    }
}

async function pollForCompletion(initiateData: MobileIdCeremonyInitiateResponse) {
    const {url, init} = props.apiConfig.completeRequest(initiateData);
    const outcome = await shortPoll.run(url, init);

    if (outcome.aborted) {
        verificationCode.value = null;
        return;
    }

    if (outcome.data) {
        emit('success', {
            phoneNumber: phoneNumber.value,
            identityNumber: identityNumber.value,
            completion: outcome.data
        });
        return;
    }

    // Any non-success outcome terminates the ceremony — the session is one-shot. Bounce the user
    // back to the form so they can retry; the error message stays visible via shortPoll.errorCode.
    verificationCode.value = null;
}

function cancel() {
    shortPoll.abort();
    verificationCode.value = null;
}

onBeforeUnmount(() => {
    shortPoll.abort();
});

defineExpose<MobileIdCeremonyHandle>({
    start,
    cancel,
    validate,
    isBusy: () => isBusyState.value
});
</script>

<template>
    <div class="mobile-id-ceremony">
        <template v-if="!isWaiting && !verificationCode">
            <LoginFormField :label="t('lbl-phoneno')" :html-for="`${testIdPrefix}-phone`" :error="phoneError">
                <div class="phone-input-row">
                    <span class="phone-input-prefix" :class="{'input--invalid': phoneError}">+372</span>
                    <input
                        :id="`${testIdPrefix}-phone`"
                        ref="phoneInput"
                        v-model="phoneNumber"
                        type="tel"
                        autocomplete="tel"
                        class="phone-input phone-input--with-prefix"
                        :class="{'input--invalid': phoneError}"
                        :disabled="isInitiating"
                        :data-test="`${testIdPrefix}-phone`"
                    />
                </div>
            </LoginFormField>
            <LoginFormField
                v-if="showPersonalCodeField"
                :label="t('lbl-personalcode')"
                :html-for="`${testIdPrefix}-identity`"
                :error="identityError"
            >
                <input
                    :id="`${testIdPrefix}-identity`"
                    ref="identityInput"
                    v-model="identityNumber"
                    type="text"
                    autocomplete="off"
                    class="identity-input"
                    :class="{'input--invalid': identityError}"
                    :disabled="isInitiating"
                    :data-test="`${testIdPrefix}-identity`"
                />
            </LoginFormField>
        </template>

        <template v-else>
            <div class="verification-prompt" :data-test="`${testIdPrefix}-verification`">
                <div class="verification-prompt__caption">
                    {{ t('lbl-ident-mob-challenge-id') }}
                </div>
                <div class="verification-prompt__code" :data-test="`${testIdPrefix}-verification-code`">
                    {{ verificationCode }}
                </div>
                <div class="verification-prompt__waiting">
                    {{ t('lbl-ident-waiting-mobile-reply') }}
                </div>
            </div>
        </template>

        <div v-if="errorMessage" class="error-div" :data-test="`${testIdPrefix}-error`">{{ errorMessage }}</div>

        <button
            v-if="isWaiting || verificationCode"
            type="button"
            class="cancel-button btn"
            :disabled="!isWaiting"
            :data-test="`${testIdPrefix}-cancel`"
            @click="cancel"
        >
            {{ t('btn-cancel') }}
        </button>
    </div>
</template>

<style scoped>
.mobile-id-ceremony {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.phone-input,
.identity-input {
    height: 36px;
    padding: 0 12px;
    margin: 0;
    border: 1px solid #ddd;
    border-radius: 1px;
    font-size: 14px;
    background: #fff;
    box-sizing: border-box;
}

.phone-input:focus,
.identity-input:focus {
    outline: none;
    border-color: #2eb336;
}

/* Bottom-only 2px red underline; box-sizing keeps the 36px outer box.
   Prefix span gets the matching underline so the fused [+372][number]
   control reads as one invalid field. */
.phone-input.input--invalid,
.identity-input.input--invalid,
.phone-input-prefix.input--invalid {
    border-bottom-color: #c1272d;
    border-bottom-width: 2px;
}

/* [+372][number] phone input: a flex row where the prefix is a non-editable
   pill on the left fused into the number field on the right via a shared
   border. */
.phone-input-row {
    display: flex;
    align-items: stretch;
}

.phone-input-prefix {
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #ddd;
    border-right: none;
    border-radius: 1px 0 0 1px;
    background: #f5f5f5;
    color: #555;
    font-size: 14px;
    user-select: none;
    box-sizing: border-box;
}

.phone-input--with-prefix {
    flex: 1;
    min-width: 0;
    border-radius: 0 1px 1px 0;
}

.error-div {
    color: #c1272d;
    min-height: 20px;
}

.verification-prompt {
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
}

.verification-prompt__caption {
    font-weight: 600;
}

.verification-prompt__code {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: 4px;
}
</style>
